import { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { downloadProjectWorkbook } from '../../../services/projects';
import { useAuth } from '../../../contexts/AuthContext';
import { useProject } from '../../../contexts/ProjectContext';

interface ExportDownloadButtonProps {
  variant?: 'text' | 'outlined' | 'contained';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
}

type DownloadState = 'idle' | 'loading' | 'success' | 'error';

export default function WorkbookDownloadButton({
  variant = 'contained',
  size = 'medium',
  fullWidth = false,
}: ExportDownloadButtonProps) {
  const [downloadState, setDownloadState] = useState<DownloadState>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { currentOrganisation, organisationId } = useAuth();
  const { currentProject, currentProjectId } = useProject();

  const handleDownload = async () => {
    // Validate required data
    if (!organisationId || !currentProjectId) {
      setDownloadState('error');
      setErrorMessage('No project selected');
      setTimeout(() => {
        setDownloadState('idle');
        setErrorMessage('');
      }, 3000);
      return;
    }

    setDownloadState('loading');
    setErrorMessage('');

    try {
      const blob = await downloadProjectWorkbook(organisationId, currentProjectId);

      // Create filename using context data
      const safeName = currentOrganisation?.name.replace(/[^a-zA-Z0-9]/g, '_') || 'RD_Export';
      const taxYear = currentProject?.taxYear || '';
      const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
      const filename = `${safeName}_${taxYear}_RDExport_${date}.xlsx`;

      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Show success state
      setDownloadState('success');

      // Reset to idle after 2 seconds
      setTimeout(() => {
        setDownloadState('idle');
      }, 2000);
    } catch (error) {
      console.error('Export download error:', error);
      setDownloadState('error');
      setErrorMessage(error instanceof Error ? error.message : 'Download failed');

      // Reset to idle after 3 seconds
      setTimeout(() => {
        setDownloadState('idle');
        setErrorMessage('');
      }, 3000);
    }
  };

  const getButtonContent = () => {
    switch (downloadState) {
      case 'loading':
        return (
          <>
            <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
            Generating Export...
          </>
        );
      case 'success':
        return (
          <>
            <CheckCircleIcon sx={{ mr: 1 }} />
            Downloaded
          </>
        );
      case 'error':
        return (
          <>
            <ErrorIcon sx={{ mr: 1 }} />
            {errorMessage || 'Download Failed'}
          </>
        );
      default:
        return (
          <>
            <DownloadIcon sx={{ mr: 1 }} />
            Download Workbook
          </>
        );
    }
  };

  const getButtonColor = () => {
    switch (downloadState) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'primary';
    }
  };

  // Disable button if no project is selected
  const isDisabled = downloadState === 'loading' || !currentProjectId || !organisationId;

  return (
    <Button
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      color={getButtonColor()}
      onClick={handleDownload}
      disabled={isDisabled}
      sx={{
        minWidth: downloadState === 'error' ? 200 : 160,
        transition: 'all 0.3s ease',
      }}
    >
      {getButtonContent()}
    </Button>
  );
}
